import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
// Autocomplete moved into AssetGroupDialog
import { useLocation } from "react-router-dom";
// import bfData from "./data/bf.json";
import {
  getBusinessFunctions,
  assetGroupList,
  updateOrgaAssets,
} from "../../redux/actions/businessFunctions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AssetGroupDialog from "./AssetGroupDialog";

// Simple demo component that uses the provided fake data to let the user
// select a business function and view its details.
export default function BusinessFunctionsPlaceholder({ editMode }) {
  // Local editable copy of the data so changes don't mutate imported JSON directly

  const orgaProfileData =
    useSelector((state) => state.organizationProfile.organizationMetricsById) ||
    {};
  const validationEnabled =
    typeof orgaProfileData?.validation_status === "boolean"
      ? orgaProfileData.validation_status
      : true;
  // Initialize empty; will be set once bfData arrives
  const [selectedId, setSelectedId] = useState("");

  const bfData = useSelector(
    (state) => state.businessFunctions.businessFunctions
  );
  const [items, setItems] = useState(() =>
    Array.isArray(bfData)
      ? bfData.map((b) => ({
          ...b,
          asset_groups: (b.asset_groups || []).map((g) => ({
            ...g,
            description: g.description || "",
          })),
        }))
      : []
  );

  // Asset groups fetching moved into AssetGroupDialog

  const orgaId = localStorage.getItem("orgaId");
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getBusinessFunctions(orgaId));
  }, [orgaId, dispatch]);

  // Asset group list fetching removed; handled inside AssetGroupDialog

  // Sync local items and selection whenever Redux bfData updates (e.g., orga change)
  useEffect(() => {
    // If bfData is not yet an array (e.g., during loading/transient updates), keep current view
    if (!Array.isArray(bfData)) return;

    const nextItems = bfData.map((b) => ({
      ...b,
      asset_groups: (b.asset_groups || []).map((g) => ({
        ...g,
        description: g.description || "",
      })),
    }));

    setItems(nextItems);

    // Ensure selectedId points to an existing BF; default to first
    if (!nextItems.some((b) => b.bf_id === selectedId)) {
      setSelectedId(nextItems[0]?.bf_id || "");
    }

    // Clear pending ops since data source changed
    payloadRef.current.ops = [];
    setOpsVersion((v) => v + 1);

    // Clear local pending deletions as well (backend is source of truth now)
    setLocallyDeletedAssetGroupsByBf({});
  }, [bfData]);

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [lastBfDeleteOpen, setLastBfDeleteOpen] = useState(false);
  // Add asset group dialog state
  const [agOpen, setAgOpen] = useState(false);
  // State for inline editing of business function names
  const [editingBfId, setEditingBfId] = useState(null);
  const [editingBfName, setEditingBfName] = useState("");
  const { t } = useTranslation();

  // Derive edit-mode gates from validation/profile state
  const location = useLocation();
  const canEnableEditMode =
    validationEnabled &&
    orgaProfileData?.reconnaissance_status === "completed" &&
    !orgaProfileData?.reconnaissance_validation;
  const navigationRequestedEditMode =
    location?.state?.editMode === true || Boolean(editMode);
  // Local toggle for edit mode controlled by button
  const [isEditMode, setIsEditMode] = useState(
    () => canEnableEditMode && navigationRequestedEditMode
  );
  useEffect(() => {
    if (!canEnableEditMode && isEditMode) {
      setIsEditMode(false);
    }
  }, [canEnableEditMode, isEditMode]);

  // Operations accumulator (not persisted) to capture all create/update/delete actions
  const payloadRef = useRef({ ops: [] });
  const [opsVersion, setOpsVersion] = useState(0); // force re-render when ops change

  // Track asset groups deleted locally (per BF) so they can immediately re-appear
  // in the "Add Asset Group" dialog options before saving to backend.
  const [locallyDeletedAssetGroupsByBf, setLocallyDeletedAssetGroupsByBf] =
    useState({});

  const addOp = (op) => {
    const ops = payloadRef.current.ops;
    const last = ops[ops.length - 1];

    // Coalesce consecutive updates on same BF
    if (
      op.type === "update_bf" &&
      last &&
      last.type === "update_bf" &&
      last.bf_id === op.bf_id
    ) {
      ops[ops.length - 1] = {
        ...last,
        changes: { ...last.changes, ...op.changes },
      };
    }
    // Coalesce consecutive updates on same asset group
    else if (
      op.type === "update_ag" &&
      last &&
      last.type === "update_ag" &&
      last.asset_group_id === op.asset_group_id
    ) {
      ops[ops.length - 1] = {
        ...last,
        changes: { ...last.changes, ...op.changes },
      };
    } else {
      ops.push(op);
    }
    setOpsVersion((v) => v + 1);
  };

  const selected = useMemo(
    () => items.find((b) => b.bf_id === selectedId) || null,
    [items, selectedId]
  );

  const updateSelectedField = (field, value) => {
    setItems((prev) =>
      prev.map((b) => (b.bf_id === selectedId ? { ...b, [field]: value } : b))
    );
    if (selectedId) {
      addOp({
        type: "update_bf",
        bf_id: selectedId,
        changes: { [field]: value },
      });
    }
  };

  const handleCreateBF = () => {
    if (!newName.trim()) return;
    const newBF = {
      bf_id: `manual-bf-${Date.now()}`,
      bf_name: newName.trim(),
      description: "",
      impact_if_disrupted: "",
      asset_groups: [],
    };
    setItems((prev) => [...prev, newBF]);
    setSelectedId(newBF.bf_id);
    setNewName("");
    setAddOpen(false);
    addOp({ type: "create_bf", bf: newBF });
  };

  const requestDeleteBF = (bfId) => {
    if (!bfId) return;
    setSelectedId(bfId);
    if (items.length <= 1) {
      setLastBfDeleteOpen(true);
      return;
    }
    setDeleteOpen(true);
  };

  const handleDeleteBF = () => {
    if (!selectedId) return;
    if (items.length <= 1) {
      setDeleteOpen(false);
      setLastBfDeleteOpen(true);
      return;
    }
    // capture name before deletion for logging
    const toDelete = items.find((b) => b.bf_id === selectedId);
    setItems((prev) => {
      const idx = prev.findIndex((b) => b.bf_id === selectedId);
      const updated = prev.filter((b) => b.bf_id !== selectedId);
      // decide new selection
      if (updated.length === 0) {
        setSelectedId("");
      } else if (idx >= updated.length) {
        setSelectedId(updated[updated.length - 1].bf_id);
      } else {
        setSelectedId(updated[idx].bf_id);
      }
      return updated;
    });
    addOp({ type: "delete_bf", bf_id: selectedId, bf_name: toDelete?.bf_name });
    setDeleteOpen(false);
  };

  const handleStartEditBfName = (bfId, currentName) => {
    setEditingBfId(bfId);
    setEditingBfName(currentName);
  };

  const handleSaveBfName = () => {
    if (!editingBfId || !editingBfName.trim()) {
      setEditingBfId(null);
      setEditingBfName("");
      return;
    }
    setItems((prev) =>
      prev.map((b) =>
        b.bf_id === editingBfId ? { ...b, bf_name: editingBfName.trim() } : b
      )
    );
    addOp({
      type: "update_bf",
      bf_id: editingBfId,
      changes: { bf_name: editingBfName.trim() },
    });
    setEditingBfId(null);
    setEditingBfName("");
  };

  const handleCancelEditBfName = () => {
    setEditingBfId(null);
    setEditingBfName("");
  };

  const handleAddAssetGroup = ({
    name,
    value,
    justification,
    description,
    assetGroupId,
  }) => {
    if (!selectedId || !name?.trim()) return;
    const parsed = parseInt(value, 10);
    const numericValue = Math.max(
      1,
      Math.min(10, Number.isFinite(parsed) ? parsed : 1)
    );
    // Use ID resolved by dialog; fallback to manual
    const selectedAssetGroupId = assetGroupId ?? `manual-ag-${Date.now()}`;
    const newAG = {
      asset_group_id: String(selectedAssetGroupId),
      name: name.trim(),
      value: numericValue,
      justification: justification?.trim() || "",
      description: description?.trim() || "",
    };
    setItems((prev) =>
      prev.map((b) =>
        b.bf_id === selectedId
          ? { ...b, asset_groups: [...(b.asset_groups || []), newAG] }
          : b
      )
    );
    setAgOpen(false);
    addOp({ type: "create_ag", bf_id: selectedId, ag: newAG });

    // If user re-added a locally deleted group, remove it from the local restore list.
    setLocallyDeletedAssetGroupsByBf((prev) => {
      const current = Array.isArray(prev[selectedId]) ? prev[selectedId] : [];
      const next = current.filter(
        (g) =>
          String(g?.id) !== String(newAG.asset_group_id) &&
          (g?.name || "").trim().toLowerCase() !==
            (newAG.name || "").trim().toLowerCase()
      );
      if (next.length === current.length) return prev;
      return { ...prev, [selectedId]: next };
    });
  };

  const handleUpdateAssetGroupValue = (assetGroupId, value) => {
    const parsed = Number(value);
    const numeric = Math.max(
      1,
      Math.min(10, Number.isFinite(parsed) ? parsed : 1)
    );
    setItems((prev) =>
      prev.map((b) =>
        b.bf_id === selectedId
          ? {
              ...b,
              asset_groups: (b.asset_groups || []).map((g) =>
                g.asset_group_id === assetGroupId ? { ...g, value: numeric } : g
              ),
            }
          : b
      )
    );
    if (selectedId) {
      addOp({
        type: "update_ag",
        bf_id: selectedId,
        asset_group_id: assetGroupId,
        changes: { value: numeric },
      });
    }
  };

  const handleDeleteAssetGroup = (assetGroupId) => {
    if (!selectedId) return;
    const toDelete = items
      .find((b) => b.bf_id === selectedId)
      ?.asset_groups?.find((g) => g.asset_group_id === assetGroupId);
    setItems((prev) =>
      prev.map((b) =>
        b.bf_id === selectedId
          ? {
              ...b,
              asset_groups: (b.asset_groups || []).filter(
                (g) => g.asset_group_id !== assetGroupId
              ),
            }
          : b
      )
    );
    addOp({
      type: "delete_ag",
      bf_id: selectedId,
      asset_group_id: assetGroupId,
      name: toDelete?.name,
    });

    // Make this deleted group immediately selectable again in the Add dialog.
    if (toDelete?.name && assetGroupId) {
      setLocallyDeletedAssetGroupsByBf((prev) => {
        const current = Array.isArray(prev[selectedId]) ? prev[selectedId] : [];
        const exists = current.some(
          (g) =>
            String(g?.id) === String(assetGroupId) ||
            (g?.name || "").trim().toLowerCase() ===
              (toDelete.name || "").trim().toLowerCase()
        );
        if (exists) return prev;
        return {
          ...prev,
          [selectedId]: [
            ...current,
            { id: String(assetGroupId), name: String(toDelete.name) },
          ],
        };
      });
    }
  };

  function save() {
    // const payload = {
    //   business_functions: items,
    //   ops: payloadRef.current.ops,
    //   generated_at: new Date().toISOString(),
    // };

    dispatch(updateOrgaAssets(orgaId, payloadRef.current.ops)).then((res) => {
      // After successful update, clear ops
      if (res) {
        payloadRef.current.ops = [];
        setOpsVersion((v) => v + 1);
        // Refresh data to keep UI in sync with backend
        dispatch(getBusinessFunctions(orgaId));
      }
    });
  }

  // Collect unique existing asset group names and associated first-found description
  const assetGroupNameDescriptionMap = useMemo(() => {
    const map = new Map();
    items.forEach((b) =>
      (b.asset_groups || []).forEach((g) => {
        if (g.name && !map.has(g.name)) {
          map.set(g.name, g.description || "");
        }
      })
    );
    return map; // Map<name, description>
  }, [items]);
  // assetGroupNames derived inside AssetGroupDialog

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "100%",
        display: "flex",
        gap: 3,
      }}
    >
      {/* Left sidebar with vertical tabs (hidden on extra-small screens) */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          minWidth: { xs: 150, sm: 180, md: 220 },
          maxWidth: { xs: 150, sm: 180, md: 220 },
          flexDirection: "column",
          gap: 2,
        }}
      >
        {isEditMode && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={() => setAddOpen(true)}
              fullWidth
            >
              Add Business Function
            </Button>
          </Box>
        )}
        <Box
          sx={{
            borderRight: 1,
            borderColor: "divider",
            maxHeight: { xs: "400px", sm: "500px", md: "600px" },
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "1px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "1px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          }}
        >
          {items.map((bf) => (
            <Box
              key={bf.bf_id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: { xs: 1, sm: 2 },
                py: { xs: 1, sm: 1.5 },
                cursor: "pointer",
                backgroundColor:
                  selectedId === bf.bf_id ? "action.selected" : "transparent",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                borderLeft: selectedId === bf.bf_id ? 3 : 0,
                borderLeftColor: "primary.main",
                minHeight: { xs: "48px", sm: "56px" },
              }}
              onClick={() =>
                editingBfId !== bf.bf_id && setSelectedId(bf.bf_id)
              }
            >
              {editingBfId === bf.bf_id ? (
                <>
                  <TextField
                    value={editingBfName}
                    onChange={(e) => setEditingBfName(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveBfName();
                      if (e.key === "Escape") handleCancelEditBfName();
                    }}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleSaveBfName}
                    color="primary"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEditBfName}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      flex: 1,
                      textAlign: "left",
                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.05rem" },
                      fontWeight: selectedId === bf.bf_id ? 500 : 400,
                    }}
                  >
                    {bf.bf_name}
                  </Typography>
                  {isEditMode && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEditBfName(bf.bf_id, bf.bf_name);
                        }}
                        sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteBF(bf.bf_id);
                        }}
                        sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Main content area */}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Top horizontal list for extra-small screens */}
        <Box sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: "2px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "1px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "rgba(0,0,0,0.3)",
              },
            }}
          >
            {isEditMode && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setAddOpen(true)}
                sx={{ flex: "0 0 auto" }}
              >
                Add
              </Button>
            )}
            {items.map((bf) => (
              <Box
                key={bf.bf_id}
                sx={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 9999,
                  border: 1,
                  borderColor:
                    selectedId === bf.bf_id ? "primary.main" : "divider",
                  backgroundColor:
                    selectedId === bf.bf_id
                      ? "action.selected"
                      : "background.paper",
                  cursor: "pointer",
                }}
                onClick={() =>
                  editingBfId !== bf.bf_id && setSelectedId(bf.bf_id)
                }
              >
                {editingBfId === bf.bf_id ? (
                  <>
                    <TextField
                      value={editingBfName}
                      onChange={(e) => setEditingBfName(e.target.value)}
                      size="small"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveBfName();
                        if (e.key === "Escape") handleCancelEditBfName();
                      }}
                      sx={{
                        minWidth: 140,
                        "& .MuiInputBase-input": {
                          py: 0.5,
                          fontSize: "0.85rem",
                        },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveBfName();
                      }}
                      color="primary"
                    >
                      <CheckIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEditBfName();
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: selectedId === bf.bf_id ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {bf.bf_name}
                    </Typography>
                    {isEditMode && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEditBfName(bf.bf_id, bf.bf_name);
                          }}
                          sx={{
                            ml: 0.25,
                            opacity: 0.8,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDeleteBF(bf.bf_id);
                          }}
                          sx={{ opacity: 0.8, "&:hover": { opacity: 1 } }}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        {selected ? (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }} variant="outlined">
            {canEnableEditMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      sm: "flex-end",
                      md: "flex-end",
                    },
                  }}
                >
                  <Button
                    variant={isEditMode ? "outlined" : "contained"}
                    color={isEditMode ? "secondary" : "primary"}
                    size="small"
                    onClick={() => setIsEditMode((v) => !v)}
                  >
                    {isEditMode ? "Disable Edit" : "Enable Edit"}
                  </Button>
                </Box>
              )}
            {isEditMode ? (
              <Box
                sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  label="Description"
                  value={selected.description}
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  onChange={(e) =>
                    updateSelectedField("description", e.target.value)
                  }
                />
                <TextField
                  label="Impact if disrupted"
                  value={selected.impact_if_disrupted}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  onChange={(e) =>
                    updateSelectedField("impact_if_disrupted", e.target.value)
                  }
                />
              </Box>
            ) : (
              <>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    mb: 1,
                  }}
                >
                  {selected.bf_name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                >
                  {selected.description}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ mt: 1, mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                >
                  Impact if disrupted
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 2, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                >
                  {selected.impact_if_disrupted}
                </Typography>
              </>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle2">Asset groups</Typography>
              {isEditMode && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setAgOpen(true)}
                  disabled={!selected}
                >
                  Add Asset Group
                </Button>
              )}
            </Box>
            {Array.isArray(selected.asset_groups) && (
              <>
                {/* Mobile stacked view */}
                <Box
                  sx={{
                    display: { xs: "block", sm: "none" },
                    maxHeight: "420px",
                    overflowY: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.5,
                    "&::-webkit-scrollbar": { width: "1px" },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.2)",
                      borderRadius: "1px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  {selected.asset_groups.map((g, idx) => (
                    <Box
                      key={g.asset_group_id}
                      sx={{
                        pt: idx > 0 ? 1.5 : 0,
                        pb: 1.5,
                        borderTop: idx > 0 ? 1 : 0,
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Name</Typography>
                        {isEditMode && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteAssetGroup(g.asset_group_id)
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {g.name}
                      </Typography>

                      <Typography variant="subtitle2">Definition</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {g.definition || "—"}
                      </Typography>

                      {/* <Typography variant="subtitle2">
                        Estimated value(/10)
                      </Typography> */}
                      
                      {/* <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        {isEditMode ? (
                          <TextField
                            type="number"
                            value={g.value}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^(?:[1-9]|10)$/.test(val)) {
                                handleUpdateAssetGroupValue(
                                  g.asset_group_id,
                                  val
                                );
                              }
                            }}
                            onKeyDown={(e) => {
                              if (
                                ["e", "E", "+", "-", ".", ","].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                            size="small"
                            inputProps={{
                              min: 1,
                              max: 10,
                              step: 1,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              style: {
                                padding: "2px 6px",
                                textAlign: "center",
                                width: 72,
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body2">{g.value}</Typography>
                        )}
                      </Box> */}

                      <Typography variant="subtitle2">Justification</Typography>
                      <Typography variant="body2">{g.justification}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Table view for sm+ */}
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    maxHeight: "420px", // Approximately 6 rows + header
                    overflowY: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    "&::-webkit-scrollbar": { width: "1px" },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.2)",
                      borderRadius: "1px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Table
                    size="medium"
                    aria-label="asset groups table"
                    sx={{ fontSize: { xs: "0.85rem", sm: "0.95rem" } }}
                    stickyHeader
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            backgroundColor: "background.paper",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: { xs: 180, sm: 220 },
                          }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            backgroundColor: "background.paper",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: { xs: 240, sm: 360 },
                          }}
                        >
                          Definition
                        </TableCell>
                        {/* <TableCell
                          sx={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            backgroundColor: "background.paper",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                          align="center"
                        >
                          Estimated value(/10)
                        </TableCell> */}
                        <TableCell
                          sx={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            backgroundColor: "background.paper",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: { xs: 240, sm: 360 },
                          }}
                        >
                          Justification
                        </TableCell>
                        {isEditMode && (
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              width: 56,
                              backgroundColor: "background.paper",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                            }}
                            align="center"
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selected.asset_groups.map((g) => (
                        <TableRow key={g.asset_group_id}>
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              whiteSpace: "normal",
                              maxWidth: { xs: 180, sm: 220 },
                              width: { xs: 180, sm: 220 },
                              wordBreak: "break-word",
                            }}
                          >
                            {g.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              whiteSpace: "normal",
                              maxWidth: { xs: 240, sm: 360 },
                              width: { xs: 240, sm: 360 },
                              wordBreak: "break-word",
                            }}
                          >
                            {g.definition || "—"}
                          </TableCell>
                          {/* <TableCell
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                            align="center"
                          >
                            {isEditMode ? (
                              <TextField
                                type="number"
                                value={g.value}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^(?:[1-9]|10)$/.test(val)) {
                                    handleUpdateAssetGroupValue(
                                      g.asset_group_id,
                                      val
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (
                                    ["e", "E", "+", "-", ".", ","].includes(
                                      e.key
                                    )
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                size="small"
                                inputProps={{
                                  min: 1,
                                  max: 10,
                                  step: 1,
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  style: {
                                    padding: "2px 6px",
                                    textAlign: "center",
                                    width: 72,
                                  },
                                }}
                              />
                            ) : (
                              g.value
                            )}
                          </TableCell> */}
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              whiteSpace: "normal",
                              maxWidth: { xs: 240, sm: 360 },
                              width: { xs: 240, sm: 360 },
                              wordBreak: "break-word",
                            }}
                          >
                            {g.justification}
                          </TableCell>
                          {isEditMode && (
                            <TableCell align="center">
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteAssetGroup(g.asset_group_id)
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Paper>
        ) : (
          <Typography>No business function selected</Typography>
        )}
        {isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!payloadRef.current.ops.length}
              onClick={() => save()}
            >
              Save Changes ({payloadRef.current.ops.length})
            </Button>
          </Box>
        )}
        <Dialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Add Business Function</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              onClick={handleCreateBF}
              variant="contained"
              size="small"
              disabled={!newName.trim()}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{t("delete_bf")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("confirm_delete_bf")} <strong>{selected?.bf_name}</strong>?{" "}
              <br />
              {t("cont_go_back")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)} size="small">
              {t("cancel")}
            </Button>
            <Button
              onClick={handleDeleteBF}
              color="error"
              variant="contained"
              size="small"
              disabled={!selectedId}
            >
              {t("delete")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={lastBfDeleteOpen}
          onClose={() => setLastBfDeleteOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {t("cannot_delete_bf_title", {
              defaultValue: "Cannot Delete Business Function",
            })}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("cannot_delete_last_bf", {
                defaultValue:
                  "You cannot delete the last business function. Add another business function before deleting this one.",
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLastBfDeleteOpen(false)} size="small">
              {t("ok", { defaultValue: "OK" })}
            </Button>
          </DialogActions>
        </Dialog>
        <AssetGroupDialog
          open={agOpen}
          onClose={() => setAgOpen(false)}
          onSubmit={(data) => handleAddAssetGroup(data)}
          bfId={selectedId}
          orgaId={orgaId}
          getDescriptionForName={(name) =>
            assetGroupNameDescriptionMap.get(name) || ""
          }
          extraOptions={
            Array.isArray(locallyDeletedAssetGroupsByBf[selectedId])
              ? locallyDeletedAssetGroupsByBf[selectedId]
              : []
          }
          excludeNames={
            Array.isArray(selected?.asset_groups)
              ? selected.asset_groups.map((g) => g?.name).filter(Boolean)
              : []
          }
        />
      </Box>
    </Box>
  );
}
